/**
 * App — 챗봇 위젯 루트 컴포넌트
 *
 * 책임:
 * - 챗봇 초기화 (useAuth)
 * - Q&A 데이터 로드
 * - 인사 메시지 출력
 * - Launcher와 ChatWindow 렌더링
 */

import { useEffect, useMemo } from 'react';
import { useChatStore } from '@/stores/chatStore';
import { useAuthStore } from '@/stores/authStore';
import { useAuth } from '@/features/auth/useAuth';
import { useChat } from '@/features/chat/useChat';
import { Launcher } from '@/components/widget/Launcher';
import { ChatWindow } from '@/components/widget/ChatWindow';
import type { QAEntry } from '@/types/qa';

// 임시 Q&A 데이터 (실제로는 빌드 시 import 또는 fetch로 로드)
import qaPublicJson from '@/data/qa-public.json';

function App() {
  // 인증 초기화
  useAuth();

  const isOpen = useChatStore((s) => s.isOpen);
  const messages = useChatStore((s) => s.messages);
  const toggle = useChatStore((s) => s.toggle);
  const close = useChatStore((s) => s.close);
  const addMessage = useChatStore((s) => s.addMessage);
  const isInitialized = useAuthStore((s) => s.isInitialized);
  const authStatus = useAuthStore((s) => s.status);
  const user = useAuthStore((s) => s.user);

  // Q&A 데이터를 타입에 맞게 캐스팅
  const qaDatabase = useMemo(() => qaPublicJson as unknown as QAEntry[], []);

  const { handleSend, handleSelectByIntent } = useChat({ qaDatabase });

  // 챗봇 처음 열 때 인사말 추가
  useEffect(() => {
    if (isOpen && messages.length === 0 && isInitialized) {
      const greeting =
        authStatus === 'authenticated' && user
          ? `안녕하세요 ${user.name}님! 🌿\n\n무엇을 도와드릴까요? 자주 찾는 질문 중에서 선택하시거나, 직접 입력해 주세요.`
          : '안녕하세요! 메일링크 AI 도우미입니다. 🌿\n\n· 기능, 요금, 사용법 관련 질문에 즉시 답변\n· 본인 계정 관련 조회는 로그인 후 가능\n\n자주 찾는 질문에서 선택하시거나 자유롭게 질문해 주세요!';

      addMessage({
        role: 'bot',
        type: 'text',
        content: greeting,
      });
    }
  }, [isOpen, messages.length, isInitialized, authStatus, user, addMessage]);

  // 외부에서 열기/닫기 이벤트 리스닝 (widget-entry.ts에서 발생)
  useEffect(() => {
    const handleOpen = (): void => {
      useChatStore.getState().open();
    };
    const handleClose = (): void => {
      useChatStore.getState().close();
    };
    window.addEventListener('maillink-chatbot:open', handleOpen);
    window.addEventListener('maillink-chatbot:close', handleClose);
    return () => {
      window.removeEventListener('maillink-chatbot:open', handleOpen);
      window.removeEventListener('maillink-chatbot:close', handleClose);
    };
  }, []);

  return (
    <>
      <Launcher isOpen={isOpen} onClick={toggle} />
      <ChatWindow
        isOpen={isOpen}
        onClose={close}
        onSend={handleSend}
        onSelectByIntent={handleSelectByIntent}
      />
    </>
  );
}

export default App;
